import React, { useState, useRef, useEffect } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import { FaTimes, FaCloudUploadAlt, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaEnvelopeOpenText, FaCopy } from "react-icons/fa";

const SIDEBAR_WIDTH = 260;

export default function UploadMboxModal({ show, onClose, onUploadSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [provider, setProvider] = useState("google"); // 'google' | 'outlook' | 'other'
  const [showHelpForm, setShowHelpForm] = useState(false);
  const [helpProvider, setHelpProvider] = useState("");
  const [helpNotes, setHelpNotes] = useState("");
  const [helpSending, setHelpSending] = useState(false);
  const [helpResult, setHelpResult] = useState(null);
  const progressRef = useRef(null);
  const fileInputRef = useRef();

  // Per-agent forwarding address used by the "keep up to date" section.
  const [inboundEmail, setInboundEmail] = useState(null);
  const [inboundLoading, setInboundLoading] = useState(false);
  const [inboundError, setInboundError] = useState(null);
  const [inboundCopied, setInboundCopied] = useState(false);
  const [forwardProvider, setForwardProvider] = useState("google");
  const inboundFetchedRef = useRef(false);

  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  // Fetch (or mint) the agent's forwarding address whenever the modal opens.
  useEffect(() => {
    if (!show) return;
    if (inboundFetchedRef.current) return;
    inboundFetchedRef.current = true;
    setInboundLoading(true);
    setInboundError(null);
    handleTokenAndPostRequest(
      `${window.api_host}/get-inbound-email-address`,
      {}
    )
      .then((res) => {
        const addr = res?.data?.inbound_email_address;
        if (addr) setInboundEmail(addr);
        else setInboundError("Could not load forwarding address.");
      })
      .catch(() => {
        setInboundError("Could not load forwarding address.");
        // Allow a retry next time the modal opens.
        inboundFetchedRef.current = false;
      })
      .finally(() => {
        setInboundLoading(false);
      });
  }, [show]);

  const copyInboundEmail = async () => {
    if (!inboundEmail) return;
    try {
      await navigator.clipboard.writeText(inboundEmail);
      setInboundCopied(true);
      setTimeout(() => setInboundCopied(false), 1800);
    } catch {
      // Fallback: select-all by selecting the text node.
      setInboundCopied(false);
    }
  };

  // Warn the agent if they try to navigate away mid-upload — the multipart
  // upload only lives in this tab, so a reload/close kills it.
  useEffect(() => {
    if (!uploading) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [uploading]);

  // Real progress is driven by the multipart upload loop in handleUpload;
  // no fake timer needed. Just snap to 100% on success and clear on close.
  useEffect(() => {
    if (!uploading && result?.success) setProgress(100);
    if (!uploading && !result) setProgress(0);
  }, [uploading, result]);

  // Tracks an in-flight upload so we can abort it (modal close, error, etc.)
  // without leaving orphaned multipart uploads holding S3 storage.
  const activeUploadRef = useRef(null);
  const [bytesUploaded, setBytesUploaded] = useState(0);
  const uploadStartedAtRef = useRef(0);

  const formatEta = (seconds) => {
    if (!isFinite(seconds) || seconds <= 0) return "calculating…";
    if (seconds < 60) return `~${Math.ceil(seconds)} sec remaining`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    if (m < 60) return `~${m} min ${s} sec remaining`;
    const h = Math.floor(m / 60);
    return `~${h} hr ${m % 60} min remaining`;
  };

  const eta = (() => {
    if (!uploading || !file || bytesUploaded <= 0) return "calculating…";
    const elapsedMs = Date.now() - uploadStartedAtRef.current;
    if (elapsedMs < 1000) return "calculating…";
    const rate = bytesUploaded / (elapsedMs / 1000); // bytes/sec
    const remaining = (file.size - bytesUploaded) / rate;
    return formatEta(remaining);
  })();

  // If the modal is closed mid-upload, abort the S3 multipart so we don't
  // leave orphaned parts (also cleaned up by a 7-day lifecycle rule).
  useEffect(() => {
    if (!show && activeUploadRef.current) {
      const u = activeUploadRef.current;
      u.cancelled = true;
      activeUploadRef.current = null;
      handleTokenAndPostRequest(`${window.api_host}/mbox-upload/abort`, {
        mbox_upload_id: u.mboxUploadId,
      }).catch(() => {});
    }
  }, [show]);

  if (!show) return null;

  const handleFile = (f) => {
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    const ALLOWED = [".mbox", ".zip", ".pst", ".eml"];
    if (!ALLOWED.includes(ext)) {
      setResult({
        success: false,
        message: "Please upload a .mbox, .zip (Google Takeout), .pst (Outlook export) or .eml file.",
      });
      return;
    }
    const MAX_GB = 30;
    if (f.size > MAX_GB * 1024 * 1024 * 1024) {
      setResult({ success: false, message: `File is too large (max ${MAX_GB} GB).` });
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setBytesUploaded(0);
    uploadStartedAtRef.current = Date.now();
    setResult(null);

    let initiated = null;
    try {
      // 1) Ask the backend to open a server-keyed S3 multipart upload.
      const initRes = await handleTokenAndPostRequest(
        `${window.api_host}/mbox-upload/initiate`,
        { filename: file.name, file_size: file.size }
      );
      if (initRes?.data?.status !== "success") {
        throw new Error(initRes?.data?.error || "Failed to start upload");
      }
      const mboxUploadId = initRes.data.mbox_upload_id;
      const partSize = initRes.data.recommended_part_size || 50 * 1024 * 1024;
      initiated = { mboxUploadId, cancelled: false };
      activeUploadRef.current = initiated;

      // 2) Slice the file and upload each part directly to S3 in parallel.
      //    Each part is signed individually; the URL is scoped to that exact
      //    (bucket, key, uploadId, partNumber), so a leaked URL grants nothing
      //    beyond writing that one part of that one object.
      const partCount = Math.ceil(file.size / partSize);
      const parts = new Array(partCount);
      const bytesDone = new Array(partCount).fill(0);

      const uploadPart = async (idx) => {
        if (initiated.cancelled) throw new Error("Upload cancelled");
        const partNumber = idx + 1;
        const start = idx * partSize;
        const end = Math.min(start + partSize, file.size);
        const blob = file.slice(start, end);

        const signRes = await handleTokenAndPostRequest(
          `${window.api_host}/mbox-upload/sign-part`,
          { mbox_upload_id: mboxUploadId, part_number: partNumber }
        );
        if (signRes?.data?.status !== "success") {
          throw new Error(signRes?.data?.error || "Failed to sign part");
        }
        const putRes = await fetch(signRes.data.url, { method: "PUT", body: blob });
        if (!putRes.ok) {
          throw new Error(`Part ${partNumber} upload failed (${putRes.status})`);
        }
        const etag = putRes.headers.get("ETag") || putRes.headers.get("etag");
        if (!etag) {
          throw new Error(
            "S3 did not return an ETag. The bucket CORS configuration must expose the ETag header."
          );
        }
        parts[idx] = { PartNumber: partNumber, ETag: etag };
        bytesDone[idx] = end - start;
        const done = bytesDone.reduce((a, b) => a + b, 0);
        setBytesUploaded(done);
        setProgress(Math.min(99, Math.floor((done / file.size) * 100)));
      };

      const CONCURRENCY = 4;
      let nextIdx = 0;
      const workers = Array.from({ length: Math.min(CONCURRENCY, partCount) }, async () => {
        while (true) {
          const i = nextIdx++;
          if (i >= partCount) return;
          await uploadPart(i);
        }
      });
      await Promise.all(workers);

      if (initiated.cancelled) throw new Error("Upload cancelled");

      // 3) Tell the backend to finalize and queue processing.
      const completeRes = await handleTokenAndPostRequest(
        `${window.api_host}/mbox-upload/complete`,
        { mbox_upload_id: mboxUploadId, parts }
      );
      if (completeRes?.data?.status !== "success") {
        throw new Error(completeRes?.data?.error || "Failed to finalize upload");
      }
      setProgress(100);
      setResult({
        success: true,
        message: completeRes.data?.message || "Mailbox uploaded — processing in the background.",
      });
      setFile(null);
      activeUploadRef.current = null;
      if (onUploadSuccess) onUploadSuccess();
    } catch (err) {
      if (initiated && !initiated.cancelled) {
        initiated.cancelled = true;
        try {
          await handleTokenAndPostRequest(`${window.api_host}/mbox-upload/abort`, {
            mbox_upload_id: initiated.mboxUploadId,
          });
        } catch (_) {}
      }
      activeUploadRef.current = null;
      const msg = err?.response?.data?.error || err?.message || "Upload failed. Please try again.";
      setResult({ success: false, message: msg });
    } finally {
      setUploading(false);
    }
  };


  const handleClose = () => {
    setFile(null);
    setResult(null);
    setUploading(false);
    setProvider("google");
    setShowHelpForm(false);
    setHelpProvider("");
    setHelpNotes("");
    setHelpResult(null);
    setHelpSending(false);
    onClose();
  };

  const submitHelpRequest = async () => {
    if (!helpProvider.trim()) return;
    setHelpSending(true);
    setHelpResult(null);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/request-mbox-import-help`,
        { provider: helpProvider.trim(), notes: helpNotes.trim() }
      );
      setHelpResult({
        success: true,
        message: "Thanks — we'll be in touch within one business day to help you import your email history.",
      });
      setHelpProvider("");
      setHelpNotes("");
    } catch (err) {
      const msg = err?.response?.data?.error || "Could not send the request. Please try again.";
      setHelpResult({ success: false, message: msg });
    } finally {
      setHelpSending(false);
    }
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: "fixed", top: 0, left: isDesktop ? SIDEBAR_WIDTH : 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.45)", zIndex: 2000,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, width: "95%", maxWidth: 540,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)", padding: "32px 28px 28px",
          position: "relative", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <button
          onClick={handleClose}
          style={{
            position: "absolute", top: 16, right: 16, background: "none",
            border: "none", cursor: "pointer", padding: 4,
          }}
        >
          <FaTimes size={18} color="#9ca3af" />
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <FaEnvelopeOpenText size={22} color="#0f766e" />
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
            Import Email History
          </h2>
        </div>

        <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.6, margin: "12px 0 12px" }}>
          Upload an export of your inbox and our AI will read every conversation you've had
          with each of your contacts and write a short summary note onto their profile.
        </p>

        <div style={{
          background: "#f0fdfa", border: "1px solid #ccfbf1", borderRadius: 10,
          padding: "14px 16px", marginBottom: 14, fontSize: 13, color: "#134e4a", lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
            Get your inbox file (5 minutes)
          </div>

          {/* Provider tabs */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            {[
              { id: "google", label: "Gmail / Google Workspace" },
              { id: "outlook", label: "Outlook / Microsoft 365" },
              { id: "other", label: "Other" },
            ].map((p) => {
              const active = provider === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProvider(p.id)}
                  style={{
                    padding: "6px 12px", borderRadius: 999,
                    border: active ? "1px solid #0f766e" : "1px solid #ccfbf1",
                    background: active ? "#0f766e" : "#fff",
                    color: active ? "#fff" : "#0f766e",
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {provider === "google" && (
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}>
                Open{" "}
                <a
                  href="https://takeout.google.com/settings/takeout/custom/gmail"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "#0f766e", fontWeight: 600 }}
                >
                  Google Takeout
                </a>{" "}
                (sign in with the email account you use for clients).
              </li>
              <li style={{ marginBottom: 6 }}>
                Scroll down, click <strong>Next step</strong>, then <strong>Create export</strong>.
              </li>
              <li style={{ marginBottom: 6 }}>
                Google emails you a download link (usually within an hour). Click it and save the .zip file.
              </li>
              <li>Drag the .zip straight into the box below — no need to unzip it.</li>
            </ol>
          )}

          {provider === "outlook" && (
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}>
                Open <strong>Outlook for Windows</strong> (the desktop app, not the web).
              </li>
              <li style={{ marginBottom: 6 }}>
                Go to <strong>File → Open &amp; Export → Import/Export → Export to a file → Outlook Data File (.pst)</strong>.
              </li>
              <li style={{ marginBottom: 6 }}>
                Pick your top-level mailbox, tick <strong>Include subfolders</strong>, and save the .pst.
              </li>
              <li>Drag the .pst file into the box below.</li>
              <li style={{ marginTop: 6, fontSize: 12, color: "#475569" }}>
                On a Mac or only have web Outlook? Set up your Outlook account in{" "}
                <a
                  href="https://www.thunderbird.net/"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "#0f766e", fontWeight: 600 }}
                >
                  Thunderbird
                </a>{" "}
                via IMAP, then right-click the folder and <strong>ImportExportTools NG → Export folder → mbox</strong>.
              </li>
            </ol>
          )}

          {provider === "other" && (
            <div style={{ fontSize: 13, color: "#134e4a" }}>
              <p style={{ margin: "0 0 8px" }}>
                Most desktop email apps can produce a <strong>.mbox</strong> export — including
                Apple Mail (Mailbox → Export Mailbox), Thunderbird (with the
                ImportExportTools NG add-on), and most IMAP clients.
              </p>
              <p style={{ margin: 0 }}>
                Once you have the .mbox (or a .zip containing it), drag it into the box below.
              </p>
            </div>
          )}

          <div style={{ marginTop: 10, fontSize: 12, color: "#475569" }}>
            Stuck or using something different?{" "}
            {!showHelpForm && !helpResult?.success && (
              <button
                type="button"
                onClick={() => {
                  setShowHelpForm(true);
                  if (provider === "outlook") setHelpProvider("Outlook / Microsoft 365");
                  else if (provider === "other") setHelpProvider("");
                }}
                style={{
                  background: "none", border: "none", padding: 0,
                  color: "#0f766e", fontWeight: 600, fontSize: 12,
                  cursor: "pointer", textDecoration: "underline",
                }}
              >
                Tell us your provider
              </button>
            )}
            {!showHelpForm && !helpResult?.success && " and we'll do it for you."}
          </div>
          {showHelpForm && !helpResult?.success && (
            <div style={{ marginTop: 10, padding: 12, background: "#fff", border: "1px solid #ccfbf1", borderRadius: 8 }}>
              <input
                type="text"
                value={helpProvider}
                onChange={(e) => setHelpProvider(e.target.value)}
                placeholder="Which email provider? (e.g. Outlook 365, Bigpond, iCloud)"
                disabled={helpSending}
                style={{
                  width: "100%", padding: "8px 10px", borderRadius: 6,
                  border: "1px solid #d1d5db", fontSize: 13, marginBottom: 8,
                  boxSizing: "border-box",
                }}
              />
              <textarea
                value={helpNotes}
                onChange={(e) => setHelpNotes(e.target.value)}
                placeholder="Anything we should know? (optional)"
                disabled={helpSending}
                rows={2}
                style={{
                  width: "100%", padding: "8px 10px", borderRadius: 6,
                  border: "1px solid #d1d5db", fontSize: 13, marginBottom: 8,
                  boxSizing: "border-box", resize: "vertical", fontFamily: "inherit",
                }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={submitHelpRequest}
                  disabled={!helpProvider.trim() || helpSending}
                  style={{
                    padding: "7px 14px", borderRadius: 6, border: "none",
                    background: !helpProvider.trim() || helpSending ? "#d1d5db" : "#0f766e",
                    color: "#fff", fontSize: 13, fontWeight: 600,
                    cursor: !helpProvider.trim() || helpSending ? "default" : "pointer",
                  }}
                >
                  {helpSending ? "Sending..." : "Send request"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowHelpForm(false); setHelpResult(null); }}
                  disabled={helpSending}
                  style={{
                    padding: "7px 14px", borderRadius: 6, border: "1px solid #d1d5db",
                    background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
              {helpResult && !helpResult.success && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#991b1b" }}>{helpResult.message}</div>
              )}
            </div>
          )}
          {helpResult?.success && (
            <div style={{
              marginTop: 10, padding: "10px 12px", borderRadius: 8,
              background: "#f0fdf4", border: "1px solid #bbf7d0",
              fontSize: 13, color: "#166534", display: "flex", alignItems: "flex-start", gap: 8,
            }}>
              <FaCheckCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{helpResult.message}</span>
            </div>
          )}
        </div>

        {/* Keep it up to date — per-agent forwarding address */}
        <div style={{
          background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10,
          padding: "14px 16px", marginBottom: 14, fontSize: 13, color: "#1e3a8a", lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
            Keep it up to date automatically
          </div>
          <p style={{ margin: "0 0 10px", color: "#1e40af" }}>
            Forward all your inbound emails to the address below and we'll keep
            each contact's notes up to date as new emails arrive — no need to
            re-upload. Set it up once in your email client and you're done.
          </p>

          {inboundLoading && (
            <div style={{ fontSize: 12, color: "#1e40af", fontStyle: "italic" }}>
              Loading your forwarding address…
            </div>
          )}
          {inboundError && (
            <div style={{ fontSize: 12, color: "#991b1b" }}>{inboundError}</div>
          )}
          {inboundEmail && (
            <>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "#fff", border: "1px solid #bfdbfe", borderRadius: 8,
                padding: "8px 10px", marginBottom: 12,
              }}>
                <code
                  onClick={(e) => {
                    const range = document.createRange();
                    range.selectNodeContents(e.currentTarget);
                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);
                  }}
                  style={{
                    flex: 1, fontSize: 12, color: "#0f172a", wordBreak: "break-all",
                    fontFamily: "Menlo, Consolas, monospace", cursor: "text",
                    userSelect: "all",
                  }}
                >
                  {inboundEmail}
                </code>
                <button
                  type="button"
                  onClick={copyInboundEmail}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "5px 10px", borderRadius: 6, border: "1px solid #1d4ed8",
                    background: inboundCopied ? "#1d4ed8" : "#fff",
                    color: inboundCopied ? "#fff" : "#1d4ed8",
                    fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {inboundCopied ? <><FaCheckCircle size={11} /> Copied</> : <><FaCopy size={11} /> Copy</>}
                </button>
              </div>

              {/* Forwarding-rule provider tabs */}
              <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                {[
                  { id: "google", label: "Gmail" },
                  { id: "outlook", label: "Outlook" },
                  { id: "apple", label: "Apple Mail" },
                  { id: "other", label: "Other" },
                ].map((p) => {
                  const active = forwardProvider === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setForwardProvider(p.id)}
                      style={{
                        padding: "5px 10px", borderRadius: 999,
                        border: active ? "1px solid #1d4ed8" : "1px solid #bfdbfe",
                        background: active ? "#1d4ed8" : "#fff",
                        color: active ? "#fff" : "#1d4ed8",
                        fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>

              {forwardProvider === "google" && (
                <ol style={{ margin: 0, paddingLeft: 20, color: "#1e40af" }}>
                  <li style={{ marginBottom: 4 }}>
                    In Gmail, go to <strong>Settings (gear) → See all settings → Forwarding and POP/IMAP</strong>.
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    Click <strong>Add a forwarding address</strong> and paste the address above.
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    Gmail sends a confirmation email to that address — we'll auto-confirm it for you within a minute (or check{" "}
                    <a
                      href="https://mail.google.com/mail/u/0/#settings/fwdandpop"
                      target="_blank" rel="noreferrer"
                      style={{ color: "#1d4ed8", fontWeight: 600 }}
                    >Forwarding settings</a> later).
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    Open <strong>Filters and Blocked Addresses → Create a new filter</strong>.
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    Leave the filter fields empty (or set <strong>Has the words: in:inbox</strong>), click{" "}
                    <strong>Create filter</strong>, then tick <strong>Forward it to:</strong> and pick the address above.
                  </li>
                  <li>
                    Click <strong>Create filter</strong>. New emails will start syncing into your contact notes immediately.
                  </li>
                </ol>
              )}

              {forwardProvider === "outlook" && (
                <ol style={{ margin: 0, paddingLeft: 20, color: "#1e40af" }}>
                  <li style={{ marginBottom: 4 }}>
                    In Outlook on the web, go to <strong>Settings (gear) → Mail → Rules</strong>.
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    Click <strong>+ Add new rule</strong>. Name it "Forward to AI".
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    Set <strong>Condition: Apply to all messages</strong> (or "From: anyone").
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    Set <strong>Action: Forward to</strong> and paste the address above.
                  </li>
                  <li>
                    Save. New inbound emails will be forwarded automatically.
                  </li>
                </ol>
              )}

              {forwardProvider === "apple" && (
                <ol style={{ margin: 0, paddingLeft: 20, color: "#1e40af" }}>
                  <li style={{ marginBottom: 4 }}>
                    Open <strong>Apple Mail → Settings → Rules → Add Rule</strong>.
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    Description: "Forward to AI". Condition: <strong>Every Message</strong>.
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    Action: <strong>Forward Message → To:</strong> paste the address above.
                  </li>
                  <li>
                    Click <strong>OK</strong>, then <strong>Apply</strong> when asked. Apple Mail must be running for the rule to fire — for 24/7 forwarding, set up the rule in your provider's web UI (Gmail/Outlook/iCloud) instead.
                  </li>
                </ol>
              )}

              {forwardProvider === "other" && (
                <div style={{ color: "#1e40af" }}>
                  Most email providers support a "forward all incoming mail" rule
                  in their web settings. Create a rule with no conditions (or
                  "applies to all messages") and set the action to{" "}
                  <strong>Forward to</strong> the address above. If you can't
                  find it, send us your provider name above and we'll set it up
                  with you.
                </div>
              )}

              <div style={{ marginTop: 10, fontSize: 12, color: "#475569" }}>
                Tip: this address is unique to you — don't share it. Forwarded
                emails are summarised by AI and saved as notes only on contacts
                already in your contact list.
              </div>
            </>
          )}
        </div>

        <div style={{
          background: "#fffbeb", border: "1px solid #fef3c7", borderRadius: 10,
          padding: "10px 14px", marginBottom: 18, fontSize: 13, color: "#92400e", lineHeight: 1.5,
          display: "flex", alignItems: "flex-start", gap: 8,
        }}>
          <FaExclamationTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            Only emails to/from contacts already in your contact list will be matched and summarised.
            Processing takes a few minutes after upload.
          </span>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? "#0f766e" : "#d1d5db"}`,
            borderRadius: 12, padding: "28px 16px", textAlign: "center",
            cursor: "pointer", marginBottom: 16, transition: "border-color 0.2s",
            background: dragOver ? "#f0fdfa" : "#fafafa",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".mbox,.zip,.pst,.eml"
            style={{ display: "none" }}
            onChange={(e) => { if (e.target.files[0]) handleFile(e.target.files[0]); }}
          />
          {file ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <FaFileAlt size={16} color="#0f766e" />
              <span style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}>{file.name}</span>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>
                ({(file.size / 1024 / 1024).toFixed(1)} MB)
              </span>
            </div>
          ) : (
            <>
              <FaCloudUploadAlt size={28} color="#9ca3af" style={{ marginBottom: 6 }} />
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                Drag & drop or <span style={{ color: "#0f766e", fontWeight: 600 }}>browse</span>
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                .mbox, .zip, .pst or .eml (max 500 MB)
              </div>
            </>
          )}
        </div>

        {uploading && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>Uploading… {eta}</span>
              <span style={{ fontSize: 12, color: "#6b7280" }}>{Math.round(progress)}%</span>
            </div>
            <div style={{ width: "100%", height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
              <div style={{
                width: `${progress}%`, height: "100%",
                background: "linear-gradient(90deg, #0f766e, #10b981)",
                borderRadius: 3, transition: "width 0.2s linear",
              }} />
            </div>
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              marginTop: 10, padding: "8px 12px", borderRadius: 8,
              background: "#fffbeb", border: "1px solid #fde68a",
              fontSize: 12, color: "#92400e", lineHeight: 1.5,
            }}>
              <FaExclamationTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                <b>Keep this tab open.</b> Closing or refreshing the browser will cancel
                the upload and you'll need to start over.
              </span>
            </div>
          </div>
        )}

        {result && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            padding: "10px 14px", borderRadius: 10, marginBottom: 16,
            background: result.success ? "#f0fdf4" : "#fef2f2",
            border: result.success ? "1px solid #bbf7d0" : "1px solid #fecaca",
            fontSize: 13, color: result.success ? "#166534" : "#991b1b", lineHeight: 1.5,
          }}>
            {result.success
              ? <FaCheckCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              : <FaExclamationTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            }
            <span>{result.message}</span>
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          style={{
            width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
            background: !file || uploading ? "#d1d5db" : "linear-gradient(135deg, #0f766e 0%, #10b981 100%)",
            color: "#fff", fontSize: 15, fontWeight: 600, cursor: !file || uploading ? "default" : "pointer",
            transition: "opacity 0.2s",
          }}
        >
          {uploading ? "Uploading..." : "Import Email History"}
        </button>
      </div>
    </div>
  );
}
